class RCScroller extends HTMLElement {

  constructor() {
    super();
    this._track = null;
    this._anim = null;
    this._pos = 0;
    this._lastTs = 0;
    this._speed = 30; // px/s
    this._pauseOnHover = true;
    this._clones = [];
    this._onMouseEnter = () => this._stop();
    this._onMouseLeave = () => this._start();
    this._onResize = this._recalc.bind(this);
    this._resizeObserver = null;
  };

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;
    this._init();
  };

  disconnectedCallback() {
    this._cleanup();
  };

  _init() {
    this._track = this.querySelector('.rc-track');
    if(!this._track) return;
    const ds = this.dataset;
    const speed = Number(ds.speed || this._speed);
    this._speed = isNaN(speed) ? this._speed : speed;
    this._pauseOnHover = String(ds.pause || 'true').toLowerCase() !== 'false';

    // Ensure base styles
    this.style.overflow = 'hidden';
    this._track.style.display = 'flex';
    this._track.style.willChange = 'transform';

    // Wrap original in a container for single transform
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.willChange = 'transform';
    wrapper.className = 'rc-scroll-wrapper';
    this.insertBefore(wrapper, this._track);
    wrapper.appendChild(this._track);
    this._wrapper = wrapper;

    this._recalc();

    if(this._pauseOnHover) {
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
    };

    window.addEventListener('resize', this._onResize);

    // Observe size changes (images load, content edits) and recalc
    try {
      this._resizeObserver = new ResizeObserver(() => this._recalc());
      this._resizeObserver.observe(this._track);
    } catch (e) {};
    this._start();
  };

  _recalc() {
    // Reset position and width cache
    this._pos = 0;
    this._lastTs = 0;
    // Remove any existing clones
    if(this._clones.length) {
      this._clones.forEach((c) => c.remove());
      this._clones = [];
    };
    // Measure widths
    this._track.style.flex = '0 0 auto';
    const trackRect = this._track.getBoundingClientRect();
    const containerRect = this.getBoundingClientRect();
    // Reset transform
    this._wrapper.style.transform = 'translate3d(0,0,0)';
    // Cache total loop width
    this._loopWidth = trackRect.width;

    // If items don’t fill the viewport, duplicate until they do
    const viewportW = containerRect.width || 0;
    const tw = trackRect.width || 0;
    if(tw > 0 && viewportW > 0) {
      const repeats = Math.max(2, Math.ceil(viewportW / tw) + 1);
      for(let i = 0; i < repeats - 1; i++) {
        const clone = this._track.cloneNode(true);
        clone.classList.add('rc-track-clone');
        clone.style.flex = '0 0 auto';
        this._wrapper.appendChild(clone);
        this._clones.push(clone);
      };
    };
  };

  _start() {
    if(this._anim) return;
    const step = (ts) => {
      if(!this._lastTs) this._lastTs = ts;
      const dt = (ts - this._lastTs) / 1000;
      this._lastTs = ts;
      this._pos += this._speed * dt;
      if(this._pos >= this._loopWidth) this._pos -= this._loopWidth;
      this._wrapper.style.transform = `translate3d(${-this._pos}px, 0, 0)`;
      this._anim = requestAnimationFrame(step);
    };
    this._anim = requestAnimationFrame(step);
  };

  _stop() {
    if(this._anim) {
      cancelAnimationFrame(this._anim);
      this._anim = null;
      this._lastTs = 0;
    };
  };

  _cleanup() {
    this._stop();
    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    if(this._resizeObserver) {
      try { this._resizeObserver.disconnect(); } catch (e) {}
      this._resizeObserver = null;
    };
  };
};

if(!customElements.get('rc-scroller')) {
  customElements.define('rc-scroller', RCScroller);
};